/**
 * Feb-2026 (User-Wunsch 24 May Iter 24) — Führerschein-Klasse + Kfz-Steuer.
 *
 * Berechnet aus den vorhandenen Fahrzeug-Stammdaten die mindestens
 * erforderliche EU-Führerschein-Klasse und eine geschätzte deutsche
 * Kfz-Steuer pro Jahr.
 *
 * Quellen:
 *  - FeV §6 + EU-Richtlinie 2006/126/EG (Klassen AM, A1, A2, A, B)
 *  - KraftStG §8 — Motorräder 1,84 €/25 cm³ angefangen (alt seit 1955)
 *  - Pkw (B-Klasse) deutlich anders — bei diesem Marketplace fokussiert auf Zweiräder
 */

/**
 * Liefert die mindestens nötige EU-Führerschein-Klasse für das gegebene
 * Zweirad oder einen Hinweis-String wenn nicht eindeutig.
 *
 * @param {object} v - Fahrzeug-Daten
 * @returns {{class: string|null, label: string, hint?: string} | null}
 */
export function deriveLicenseClass(v) {
    if (!v) return null;
    const cat = (v.category || "motorcycle").toLowerCase();
    if (cat === "bicycle") {
        return { class: "—", label: "Fahrrad — kein Führerschein erforderlich" };
    }
    const cc = Number(v.displacement) || 0;
    const kw = Number(v.power_kw) || 0;
    const vmax = Number(v.max_speed_kmh) || 0;
    const cls = (v.vehicle_class || "").toUpperCase();

    // L1e (Moped/Roller bis 50 cm³, 45 km/h, 4 kW) → Klasse AM
    if (cls.startsWith("L1") || (cc > 0 && cc <= 50 && vmax > 0 && vmax <= 45)) {
        return { class: "AM", label: "AM (Mofa/Moped/Roller bis 45 km/h, 50 cm³)" };
    }
    // L3e-A1 / A1 — bis 125 cm³, 11 kW, P/G ≤ 0.1 kW/kg
    if (cc > 0 && cc <= 125 && kw > 0 && kw <= 11) {
        return { class: "A1", label: "A1 (bis 125 cm³, 11 kW)" };
    }
    // A2 — bis 35 kW, P/G ≤ 0.2 kW/kg, nicht aus ≥70 kW Modell gedrosselt
    if (kw > 0 && kw <= 35) {
        return { class: "A2", label: "A2 (bis 35 kW)" };
    }
    // A — ohne Begrenzung
    if (kw > 35) {
        return { class: "A", label: "A (über 35 kW, offen)" };
    }
    return { class: null, label: "Klasse nicht eindeutig — Hubraum + Leistung prüfen" };
}

/**
 * Schätzt die deutsche Kfz-Steuer pro Jahr für ein Zweirad.
 *
 * - Krafträder: 1,84 € je angefangene 25 cm³.
 * - Leichtkrafträder ≤ 125 cm³ und ≤ 11 kW → steuerbefreit (KraftStG §3 Nr. 1).
 * - Elektro-Motorräder → 10 Jahre steuerbefreit, danach 50 % vom Pkw-Tarif (vereinfacht 0 €).
 *
 * @returns {{amount_eur: number, label: string, monthly_eur: number} | null}
 */
export function deriveAnnualTaxEUR(v) {
    if (!v) return null;
    const cat = (v.category || "motorcycle").toLowerCase();
    if (cat === "bicycle") return null;
    const cc = Number(v.displacement) || 0;
    const kw = Number(v.power_kw) || 0;
    const fuel = (v.fuel || "").toLowerCase();

    if (fuel === "elektro") {
        return { amount_eur: 0, monthly_eur: 0, label: "Elektro — 10 Jahre steuerbefreit" };
    }
    if (cc > 0 && cc <= 125 && kw > 0 && kw <= 11) {
        return { amount_eur: 0, monthly_eur: 0, label: "Leichtkraftrad — steuerbefreit" };
    }
    if (cc <= 0) return null;
    // §8 Abs. 1 Nr. 1 b KraftStG: Motorräder 1,84 € je angefangene 25 cm³
    const units = Math.ceil(cc / 25);
    const annual = +(units * 1.84).toFixed(2);
    return {
        amount_eur: annual,
        monthly_eur: +(annual / 12).toFixed(2),
        label: "Motorrad-Tarif §8 KraftStG (1,84 € je 25 cm³)",
    };
}
